import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { axiosBaseQuery } from '@urc-ui/store/api/base';
import { HttpMethod, Tag } from '@urc-ui/store/api/types';
import {
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  DeleteSubscriptionRequest,
  DeleteSubscriptionResponse,
  EditSubscriptionRequest,
  EditSubscriptionResponse,
  GetSubscriptionByIdRequest,
  GetSubscriptionByIdResponse,
  ListSubscriptionResponse,
  ListSubscriptionsRequest,
  RunSubscriptionRequest,
  RunSubscriptionResponse
} from '@urc-ui/store/api/ross/types/api-types';
import {
  getAdvertisingApiHeaders,
  getLocaleheader,
  getMarketplaceIdHeader,
  getTimezoneHeader,
  resolveAdvertisingApiBaseUrl
} from '@urc-ui/store/api/ross/utils';

export const rossSubscriptionsApi = createApi({
  reducerPath: 'rossSubscriptionsAPI',
  baseQuery: axiosBaseQuery({
    baseUrl: resolveAdvertisingApiBaseUrl()
  }),
  tagTypes: [Tag.SUBSCRIPTIONS],
  endpoints: builder => ({
    getSubscriptionById: builder.query({
      query: (request: GetSubscriptionByIdRequest) => {
        return {
          url: `/reports/subscriptions/${request.subscriptionId}`,
          method: HttpMethod.GET,
          headers: { ...getAdvertisingApiHeaders(), ...getLocaleheader(), ...getTimezoneHeader() }
        };
      },
      transformResponse: (response: any): GetSubscriptionByIdResponse => {
        return {
          subscription: response?.subscription
        };
      },
      providesTags: (result, error, request) =>
        result
          ? [
              {
                type: Tag.SUBSCRIPTIONS,
                id: request.subscriptionId
              }
            ]
          : []
    }),
    listSubscriptions: builder.query({
      query: (request: ListSubscriptionsRequest) => {
        return {
          url: `/reports/subscriptions`,
          method: HttpMethod.POST,
          headers: { ...getAdvertisingApiHeaders(), ...getLocaleheader(), ...getTimezoneHeader() },
          data: request
        };
      },
      transformResponse: (response: any): ListSubscriptionResponse => {
        return {
          subscriptions: response?.subscriptions,
          total: response?.total,
          nextToken: response?.nextToken
        };
      },
      providesTags: [Tag.SUBSCRIPTIONS]
    }),
    createSubscription: builder.mutation({
      query: (request: CreateSubscriptionRequest) => {
        return {
          url: `/reports/subscriptions`,
          method: HttpMethod.PUT,
          headers: { ...getAdvertisingApiHeaders(), ...getLocaleheader(), ...getTimezoneHeader(), ...getMarketplaceIdHeader() },
          data: request
        };
      },
      transformResponse: (response: any): CreateSubscriptionResponse => {
        return {
          subscriptionId: response?.id
        };
      }
    }),
    editSubscription: builder.mutation({
      query: (request: EditSubscriptionRequest) => {
        return {
          url: `/reports/subscription-edits`,
          method: HttpMethod.PUT,
          headers: { ...getAdvertisingApiHeaders(), ...getLocaleheader() },
          data: request
        };
      },
      transformResponse: (response: any): EditSubscriptionResponse => {
        return {
          success: response?.successful,
          failed: response?.failed
        };
      },
      invalidatesTags: result => (result?.success ? result.success.map(id => ({ type: Tag.SUBSCRIPTIONS, id })) : [])
    }),
    deleteSubscription: builder.mutation({
      query: (request: DeleteSubscriptionRequest) => {
        return {
          url: `/reports/subscriptions`,
          method: HttpMethod.PATCH,
          headers: { ...getAdvertisingApiHeaders(), ...getLocaleheader() },
          data: request
        };
      },
      transformResponse: (response: any): DeleteSubscriptionResponse => {
        return {
          success: response?.successful,
          failed: response?.failed
        };
      }
    }),
    runSubscription: builder.mutation({
      query: (request: RunSubscriptionRequest) => {
        return {
          url: `/reports/subscriptions/${request.subscriptionId}/report`,
          method: HttpMethod.PUT,
          headers: { ...getAdvertisingApiHeaders(), ...getLocaleheader() }
        };
      },
      transformResponse: (response: any): RunSubscriptionResponse => {
        return {
          report: response?.report
        };
      }
    })
  })
});

export const {
  useGetSubscriptionByIdQuery,
  useLazyListSubscriptionsQuery,
  useCreateSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useRunSubscriptionMutation,
  useEditSubscriptionMutation
} = rossSubscriptionsApi;
